import styled, { css } from 'styled-components';
import { media } from '@styles/breakpoints';

/* ─────────────────────────────────────────────
   Eyebrow — pequeno label acima de títulos
────────────────────────────────────────────── */
export const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  letter-spacing: ${({ theme }) => theme.letterSpacings.widest};
  text-transform: uppercase;
  color: ${({ theme }) => theme.semantic.brand.accent};

  /* Linha decorativa antes do texto. */
  &::before {
    content: '';
    display: block;
    width: 24px;
    height: 2px;
    background-color: currentColor;
    flex-shrink: 0;
  }
`;

/* ─────────────────────────────────────────────
   Headings — H1 a H4 com tamanhos responsivos
────────────────────────────────────────────── */

/** H1 — Herói de página. */
export const H1 = styled.h1<{ $inverse?: boolean }>`
  font-size: ${({ theme }) => theme.fontSizes['4xl']};
  font-weight: ${({ theme }) => theme.fontWeights.extrabold};
  line-height: ${({ theme }) => theme.lineHeights.tight};
  letter-spacing: ${({ theme }) => theme.letterSpacings.tight};
  color: ${({ $inverse, theme }) =>
    $inverse ? theme.semantic.text.inverse : theme.semantic.text.primary};

  ${media.tablet} {
    font-size: ${({ theme }) => theme.fontSizes['5xl']};
  }

  ${media.desktop} {
    font-size: ${({ theme }) => theme.fontSizes['7xl']};
  }
`;

/** H2 — Título de seção. */
export const H2 = styled.h2<{ $inverse?: boolean }>`
  font-size: ${({ theme }) => theme.fontSizes['3xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  line-height: ${({ theme }) => theme.lineHeights.snug};
  letter-spacing: ${({ theme }) => theme.letterSpacings.tight};
  color: ${({ $inverse, theme }) =>
    $inverse ? theme.semantic.text.inverse : theme.semantic.text.primary};

  ${media.tablet} {
    font-size: ${({ theme }) => theme.fontSizes['4xl']};
  }

  ${media.desktop} {
    font-size: ${({ theme }) => theme.fontSizes['5xl']};
  }
`;

/** H3 — Título de subseção / card. */
export const H3 = styled.h3<{ $inverse?: boolean }>`
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  line-height: ${({ theme }) => theme.lineHeights.snug};
  letter-spacing: ${({ theme }) => theme.letterSpacings.snug};
  color: ${({ $inverse, theme }) =>
    $inverse ? theme.semantic.text.inverse : theme.semantic.text.primary};

  ${media.tablet} {
    font-size: ${({ theme }) => theme.fontSizes['3xl']};
  }
`;

/** H4 — Título auxiliar. */
export const H4 = styled.h4<{ $inverse?: boolean }>`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  line-height: ${({ theme }) => theme.lineHeights.snug};
  color: ${({ $inverse, theme }) =>
    $inverse ? theme.semantic.text.inverse : theme.semantic.text.primary};
`;

/* ─────────────────────────────────────────────
   Lead — parágrafo de destaque
────────────────────────────────────────────── */
export const Lead = styled.p<{ $inverse?: boolean }>`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
  color: ${({ $inverse, theme }) =>
    $inverse ? theme.semantic.text.inverseMuted : theme.semantic.text.secondary};
  max-width: 68ch;

  ${media.tablet} {
    font-size: ${({ theme }) => theme.fontSizes.xl};
  }
`;

/* ─────────────────────────────────────────────
   Body — parágrafo padrão
────────────────────────────────────────────── */
export const Body = styled.p<{ $size?: 'sm' | 'base' | 'lg'; $inverse?: boolean }>`
  font-size: ${({ theme, $size = 'base' }) =>
    $size === 'sm' ? theme.fontSizes.sm : $size === 'lg' ? theme.fontSizes.lg : theme.fontSizes.base};
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
  color: ${({ $inverse, theme }) =>
    $inverse ? theme.semantic.text.inverseMuted : theme.semantic.text.tertiary};
`;

/* ─────────────────────────────────────────────
   GradientText — texto com gradiente da marca
────────────────────────────────────────────── */
export const GradientText = styled.span`
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.steel[500]} 0%,
    ${({ theme }) => theme.colors.cyan[400]} 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

/* ─────────────────────────────────────────────
   SectionTitle — composição padrão para cabeçalhos de seção
────────────────────────────────────────────── */
export const SectionHeader = styled.div<{ $centered?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  ${({ $centered }) =>
    $centered &&
    css`
      align-items: center;
      text-align: center;

      ${Lead} {
        margin-inline: auto;
      }
    `}
`;
